'use client'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { v4 as uuid } from 'uuid'
import { Toast } from '../Toast'
import { ToastContext } from './ToastContext'
import { ToastItem, ToastPosition } from '../types/toast.types'
import styles from '../Toast.module.scss'

export interface ToastProviderProps {
  children: React.ReactNode
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  )

  // Create a DOM element to be used as a portal
  useEffect(() => {
    // Check if the container already exists
    let container = document.getElementById('toast-portal-container')

    // If it doesn't exist, create it
    if (!container) {
      container = document.createElement('div')
      container.id = 'toast-portal-container'
      document.body.appendChild(container)
    }

    setPortalContainer(container)

    // Cleanup on component removal
    return () => {
      // Do not remove the container if there are other Toast Providers
      // In a single page application, it may be useful to keep it
    }
  }, [])

  // Remove a specific toast - memoized to prevent re-renders
  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  // Remove all toasts - memoized to prevent re-renders
  const clearToasts = useCallback(() => {
    setToasts([])
  }, [])

  // Function to show a new toast - memoized to prevent re-renders
  const showToast = useCallback(
    (config: Omit<ToastItem, 'id'>): string => {
      const id = uuid() // Generate a unique ID for this toast

      // Create the toast object with the generated ID
      const newToast = {
        id,
        message: config.message,
        variant: config.variant || 'generic',
        position: config.position || 'top-right',
        colorScheme: config.colorScheme,
        duration: config.duration !== undefined ? config.duration : 3000, // Default 3 secondi.
        showCloseButton:
          config.showCloseButton !== undefined ? config.showCloseButton : true,
        Icon: config.Icon,
        persistent: config.persistent || false,
        hideIcon: config.hideIcon || false,
      }

      setToasts((prev) => [...prev, newToast])

      // Remove the toast automatically after the specified duration, unless it is persistent
      if (!config.persistent && config.duration !== 0) {
        setTimeout(() => {
          removeToast(id)
        }, config.duration || 3000)
      }

      return id
    },
    [removeToast]
  )

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      toasts,
      showToast,
      removeToast,
      clearToasts,
    }),
    [toasts, showToast, removeToast, clearToasts]
  )

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {portalContainer && (
        <ToastContainer
          portalContainer={portalContainer}
          toasts={toasts}
          removeToast={removeToast}
        />
      )}
    </ToastContext.Provider>
  )
}

// Internal component that renders all active toasts
interface ToastContainerProps {
  portalContainer: HTMLElement
  toasts: ToastItem[]
  removeToast: (id: string) => void
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  portalContainer,
  toasts,
  removeToast,
}) => {
  // Group toasts by position
  const toastsByPosition = toasts.reduce<Record<ToastPosition, ToastItem[]>>(
    (acc, toast) => {
      const position = toast.position || 'top-right'
      if (!acc[position]) acc[position] = []
      acc[position].push(toast)
      return acc
    },
    {} as Record<ToastPosition, ToastItem[]>
  )

  // Use createPortal to render in the container
  return createPortal(
    <>
      {Object.entries(toastsByPosition).map(([position, positionToasts]) => (
        <div
          key={position}
          className={`${styles.toastContainer} ${
            styles[`toastContainer--${position}`]
          }`}
          aria-label='Notifiche'
        >
          {positionToasts.map((toast) => (
            <Toast
              key={toast.id}
              id={toast.id}
              variant={toast.variant}
              colorScheme={toast.colorScheme}
              position={position as ToastPosition}
              duration={toast.duration}
              showCloseButton={toast.showCloseButton}
              Icon={toast.Icon}
              persistent={toast.persistent || false}
              hideIcon={toast.hideIcon || false}
              onClose={() => removeToast(toast.id)}
            >
              {toast.message}
            </Toast>
          ))}
        </div>
      ))}
    </>,
    portalContainer
  )
}
